/**
 * Badges for the Call Centre console: campaign and contact statuses, outcomes, import match statuses,
 * Planning Centre link status and the journey badges (DA, E1 to E5). Names and colours come from the
 * API (lookup tables); the fallbacks below only apply when a colour is missing.
 */

import type { ReactNode } from 'react';
import { IconCheck, IconLock, IconCloudCheck, IconClockHour4, IconLinkOff, IconCloudX } from '@tabler/icons-react';
import { Badge, Group, Tooltip, Text, Stack } from '@/components/ui';
import type { JourneyBadgeView, PersonJourneyStepView } from '@actschurch/ccms-sdk';
import {
  CALL_CENTRE_CAMPAIGN_STATUS,
  CALL_CENTRE_IMPORT_MATCH_STATUS,
  CALL_CENTRE_PCO_LINK_STATUS,
  CALL_CENTRE_HANDOFF_STATUS,
  CALL_CENTRE_SYNC_STATUS,
  badgeColour,
  formatYmd,
  formatStampDateTime,
} from './callCentreUtils';

const badgeStyles = { root: { textTransform: 'none' as const } };

type Size = 'xs' | 'sm' | 'md' | 'lg';

const CAMPAIGN_STATUS_COLOUR: Record<number, string> = {
  [CALL_CENTRE_CAMPAIGN_STATUS.Draft]: 'gray',
  [CALL_CENTRE_CAMPAIGN_STATUS.Active]: 'green',
  [CALL_CENTRE_CAMPAIGN_STATUS.Paused]: 'yellow',
  [CALL_CENTRE_CAMPAIGN_STATUS.Completed]: 'blue',
  [CALL_CENTRE_CAMPAIGN_STATUS.Archived]: 'dark',
};

export function CampaignStatusBadge({ statusId, name, color, size = 'sm' }: { statusId: number; name: string; color?: string | null; size?: Size }) {
  return (
    <Badge size={size} variant="light" color={badgeColour(color, CAMPAIGN_STATUS_COLOUR[statusId] ?? 'gray')} styles={badgeStyles}>
      {name}
    </Badge>
  );
}

export function LookupBadge({ name, color, size = 'sm', fallback = 'gray', variant = 'light', leftSection }: { name: ReactNode; color?: string | null; size?: Size; fallback?: string; variant?: 'light' | 'filled' | 'outline' | 'dot'; leftSection?: ReactNode }) {
  return (
    <Badge size={size} variant={variant} color={badgeColour(color, fallback)} styles={badgeStyles} leftSection={leftSection}>
      {name}
    </Badge>
  );
}

const MATCH_COLOUR: Record<number, string> = {
  [CALL_CENTRE_IMPORT_MATCH_STATUS.Matched]: 'green',
  [CALL_CENTRE_IMPORT_MATCH_STATUS.Possible]: 'orange',
  [CALL_CENTRE_IMPORT_MATCH_STATUS.New]: 'blue',
  [CALL_CENTRE_IMPORT_MATCH_STATUS.Duplicate]: 'gray',
  [CALL_CENTRE_IMPORT_MATCH_STATUS.Invalid]: 'red',
};

export const matchStatusColour = (id: number) => MATCH_COLOUR[id] ?? 'gray';

export function MatchStatusBadge({ statusId, name, size = 'sm' }: { statusId: number; name: string; size?: Size }) {
  return (
    <Badge size={size} variant="light" color={matchStatusColour(statusId)} styles={badgeStyles}>
      {name}
    </Badge>
  );
}

export function PcoLinkBadge({ statusId, name, size = 'sm' }: { statusId: number; name?: string | null; size?: Size }) {
  const linked = statusId === CALL_CENTRE_PCO_LINK_STATUS.Linked;
  const label = name || (linked ? 'Linked' : statusId === CALL_CENTRE_PCO_LINK_STATUS.Found ? 'Found' : statusId === CALL_CENTRE_PCO_LINK_STATUS.NotInPco ? 'Not in Planning Centre' : 'Not checked');
  return (
    <Badge size={size} variant={linked ? 'light' : 'outline'} color={linked ? 'teal' : 'gray'} styles={badgeStyles}>
      {label}
    </Badge>
  );
}

const HANDOFF_COLOUR: Record<number, string> = {
  [CALL_CENTRE_HANDOFF_STATUS.Open]: 'orange',
  [CALL_CENTRE_HANDOFF_STATUS.Accepted]: 'blue',
  [CALL_CENTRE_HANDOFF_STATUS.Done]: 'green',
  [CALL_CENTRE_HANDOFF_STATUS.Declined]: 'gray',
};

export function HandoffStatusBadge({ statusId, name, color, size = 'sm' }: { statusId: number; name: string; color?: string | null; size?: Size }) {
  return (
    <Badge size={size} variant="light" color={badgeColour(color, HANDOFF_COLOUR[statusId] ?? 'gray')} styles={badgeStyles}>
      {name}
    </Badge>
  );
}

const SYNC_COLOUR: Record<number, string> = {
  [CALL_CENTRE_SYNC_STATUS.Queued]: 'gray',
  [CALL_CENTRE_SYNC_STATUS.Running]: 'blue',
  [CALL_CENTRE_SYNC_STATUS.Done]: 'green',
  [CALL_CENTRE_SYNC_STATUS.Failed]: 'red',
  [CALL_CENTRE_SYNC_STATUS.Conflict]: 'orange',
  [CALL_CENTRE_SYNC_STATUS.Cancelled]: 'gray',
};

const syncStatusColour = (id: number, color?: string | null) => badgeColour(color, SYNC_COLOUR[id] ?? 'gray');

/** A Planning Centre sync job's or item's status (Queued, Running, Done, Failed, Conflict, Cancelled). */
export function SyncStatusBadge({ statusId, name, color, size = 'sm' }: { statusId: number; name: string; color?: string | null; size?: Size }) {
  return (
    <Badge size={size} variant={statusId === CALL_CENTRE_SYNC_STATUS.Cancelled ? 'outline' : 'light'} color={syncStatusColour(statusId, color)} styles={badgeStyles}>
      {name}
    </Badge>
  );
}

export function PastoralBadge({ size = 'xs' }: { size?: Size }) {
  return (
    <Badge size={size} variant="light" color="grape" leftSection={<IconLock size={10} />} styles={badgeStyles}>
      Pastoral
    </Badge>
  );
}

/**
 * Stage 4: where a journey entry stands with Planning Centre (pcoSyncStatusID). Null = nothing is sent for it
 * (the step has no Planning Centre sync, or the entry is not completed).
 */
function journeyPcoSync(entry: Pick<PersonJourneyStepView, 'pcoSyncStatusID' | 'pcoSyncedAt'> | null | undefined): {
  short: string;
  label: string;
  color: string;
  icon: ReactNode;
} | null {
  const id = entry?.pcoSyncStatusID;
  if (id == null) return null;
  switch (id) {
    case CALL_CENTRE_SYNC_STATUS.Done:
      return {
        short: 'Sent',
        label: `Sent to Planning Centre${entry?.pcoSyncedAt ? ` on ${formatStampDateTime(entry.pcoSyncedAt)}` : ''}`,
        color: 'green',
        icon: <IconCloudCheck size={11} />,
      };
    case CALL_CENTRE_SYNC_STATUS.Queued:
    case CALL_CENTRE_SYNC_STATUS.Running:
      return { short: 'Waiting', label: 'Waiting to be sent to Planning Centre', color: 'blue', icon: <IconClockHour4 size={11} /> };
    case CALL_CENTRE_SYNC_STATUS.Cancelled:
      return { short: 'Not linked', label: 'Not linked to Planning Centre', color: 'gray', icon: <IconLinkOff size={11} /> };
    case CALL_CENTRE_SYNC_STATUS.Failed:
    case CALL_CENTRE_SYNC_STATUS.Conflict:
      return { short: 'Not sent', label: "Couldn't be sent to Planning Centre", color: 'red', icon: <IconCloudX size={11} /> };
    default:
      return null;
  }
}

/** A small Planning Centre mark for a journey entry: Sent, Waiting or Not linked to Planning Centre. */
export function JourneyPcoIndicator({
  entry,
  size = 'xs',
  prefix = 'Planning Centre',
}: {
  entry: Pick<PersonJourneyStepView, 'pcoSyncStatusID' | 'pcoSyncedAt'> | null | undefined;
  size?: Size;
  /** Text before the status ("Planning Centre: Sent", or a step code: "E1: Sent"). */
  prefix?: string;
}) {
  const sync = journeyPcoSync(entry);
  if (!sync) return null;
  const text = sync.short === 'Not linked' ? (prefix === 'Planning Centre' ? sync.label : `${prefix}: ${sync.label}`) : `${prefix}: ${sync.short}`;
  return (
    <Tooltip label={sync.label} withArrow>
      <Badge size={size} variant="light" color={sync.color} leftSection={sync.icon} styles={badgeStyles}>
        {text}
      </Badge>
    </Tooltip>
  );
}

/** One journey step as a badge: completed steps in their colour with the year, the rest greyed out. */
export function JourneyStepBadge({ step, size = 'md' }: { step: JourneyBadgeView; size?: Size }) {
  const entry = step.entry;
  const year = entry?.completedYear ?? (entry?.completedOn ? Number(entry.completedOn.slice(0, 4)) : null);
  const label = step.isCompleted && year ? `${step.code} ${year}` : step.code;
  const pco = journeyPcoSync(entry);
  const tooltip = (
    <Stack gap={2}>
      <Text size="xs" fw={600}>
        {step.name}
      </Text>
      <Text size="xs">
        {step.isCompleted
          ? `Completed${entry?.completedOn ? ` on ${formatYmd(entry.completedOn)}` : year ? ` in ${year}` : ''}`
          : entry
            ? `${entry.statusName}${entry.bookedFor ? ` for ${formatYmd(entry.bookedFor)}` : ''}`
            : 'Not done yet'}
      </Text>
      {entry?.linkTitle && <Text size="xs">{entry.linkTitle}{entry.linkDate ? `, ${formatYmd(entry.linkDate)}` : ''}</Text>}
      {pco && <Text size="xs">{pco.label}</Text>}
    </Stack>
  );
  return (
    <Tooltip label={tooltip} withArrow multiline w={240}>
      <Badge
        size={size}
        variant={step.isCompleted ? 'filled' : entry ? 'light' : 'outline'}
        color={step.isCompleted ? badgeColour(step.color, 'blue') : entry ? badgeColour(step.color, 'blue') : 'gray'}
        leftSection={step.isCompleted ? <IconCheck size={11} /> : undefined}
        rightSection={pco ? <span style={{ display: 'inline-flex' }} aria-label={pco.label}>{pco.icon}</span> : undefined}
        styles={{ root: { textTransform: 'none', opacity: step.isCompleted || entry ? 1 : 0.55 } }}
      >
        {label}
      </Badge>
    </Tooltip>
  );
}

/**
 * The journey badges in step order. `showPco` adds a line under them with each entry's Planning Centre status
 * ("E1: Sent", "E2: Waiting", "E3: Not linked to Planning Centre") for entries that are sent there.
 */
export function JourneyBadges({ steps, size = 'md', showPco = false }: { steps: JourneyBadgeView[]; size?: Size; showPco?: boolean }) {
  const sorted = steps.slice().sort((a, b) => a.sortOrder - b.sortOrder);
  if (sorted.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No journey steps are set up yet.
      </Text>
    );
  }
  const synced = showPco ? sorted.filter((s) => journeyPcoSync(s.entry)) : [];
  const badges = (
    <Group gap={6} wrap="wrap">
      {sorted.map((s) => (
        <JourneyStepBadge key={s.stepID} step={s} size={size} />
      ))}
    </Group>
  );
  if (synced.length === 0) return badges;
  return (
    <Stack gap={6}>
      {badges}
      <Group gap={4} wrap="wrap" aria-label="Planning Centre">
        {synced.map((s) => (
          <JourneyPcoIndicator key={s.stepID} entry={s.entry} prefix={s.code} />
        ))}
      </Group>
    </Stack>
  );
}
